import { CandlestickSeries, ColorType, createChart, HistogramSeries, LineSeries, LineStyle, type IChartApi, type IPriceLine, type UTCTimestamp } from 'lightweight-charts';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { ContractSpec, FillView, InstrumentMarket, OrderView } from '@head-of-trading/contracts';
import { useLanguage } from '../../i18n/context.js';
import { getTerminalCopy } from '../../i18n/terminal.js';
import { formatInteger, formatPct, formatPrice } from '../../lib/format.js';

interface MarketChartProps {
  readonly market: InstrumentMarket;
  readonly contract: ContractSpec;
  readonly chartType: 'line' | 'candles';
  readonly fills: readonly FillView[];
  readonly orders: readonly OrderView[];
  readonly showMarkers: boolean;
  readonly canTrade: boolean;
  readonly onChartType: (value: 'line' | 'candles') => void;
  readonly onToggleMarkers: () => void;
  readonly onCancelOrder: (orderId: string) => void;
  readonly onReplaceOrder: (order: OrderView, limitPrice: number) => void;
}

const time = (value: number): UTCTimestamp => (1_700_000_000 + Math.floor(value)) as UTCTimestamp;

interface InteractiveSeries {
  priceToCoordinate(price: number): number | null;
  coordinateToPrice(coordinate: number): number | null;
  createPriceLine(options: { price: number; color?: string; lineWidth?: 1 | 2 | 3 | 4; lineStyle?: LineStyle; axisLabelVisible?: boolean; title?: string }): IPriceLine;
  removePriceLine(line: IPriceLine): void;
}

interface DragState {
  readonly orderId: string;
  readonly pointerId: number;
  readonly price: number;
}

const activeOrderStatuses = new Set(['IN_TRANSMISSION', 'ACTIVE', 'PARTIALLY_FILLED', 'CANCEL_PENDING']);

const decimalPlaces = (value: number): number => {
  const [coefficient, exponentText] = Math.abs(value).toString().toLowerCase().split('e');
  const exponent = Number(exponentText ?? 0);
  const fractionLength = coefficient?.split('.')[1]?.length ?? 0;
  return Math.max(0, fractionLength - exponent);
};

export const snapPriceToTick = (price: number, tickSize: number): number => {
  if (!Number.isFinite(price) || !Number.isFinite(tickSize) || tickSize <= 0) return price;
  const precision = Math.min(10, decimalPlaces(tickSize));
  return Number((Math.round(price / tickSize) * tickSize).toFixed(precision));
};

export function MarketChart({ market, contract, chartType, fills, orders, showMarkers, canTrade, onChartType, onToggleMarkers, onCancelOrder, onReplaceOrder }: MarketChartProps) {
  const { language } = useLanguage();
  const copy = getTerminalCopy(language).marketChart;
  const containerRef = useRef<HTMLDivElement>(null);
  const orderLayerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<InteractiveSeries | null>(null);
  const updatePriceRef = useRef<(points: InstrumentMarket['chart']) => void>(() => undefined);
  const updateVolumeRef = useRef<(points: InstrumentMarket['chart']) => void>(() => undefined);
  const [chartGeneration, setChartGeneration] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [coordinates, setCoordinates] = useState<Readonly<Record<string, number>>>({});
  const activeOrders = useMemo(() => orders.filter((order) => order.symbol === market.symbol && order.type === 'LMT' && order.limitPrice !== null && activeOrderStatuses.has(order.status)), [orders, market.symbol]);

  const refreshCoordinates = useCallback(() => {
    const series = seriesRef.current;
    if (!series) return;
    const next: Record<string, number> = {};
    for (const order of activeOrders) {
      const price = drag?.orderId === order.orderId ? drag.price : order.limitPrice;
      if (price === null) continue;
      const coordinate = series.priceToCoordinate(price);
      if (coordinate !== null) {
        const layerHeight = orderLayerRef.current?.clientHeight ?? 0;
        const minimum = 12;
        const maximum = Math.max(minimum, layerHeight - 12);
        next[order.orderId] = layerHeight > 0 ? Math.min(maximum, Math.max(minimum, Number(coordinate))) : Number(coordinate);
      }
    }
    setCoordinates(next);
  }, [activeOrders, drag]);

  const priceAtPointer = (event: ReactPointerEvent<HTMLElement>): number | null => {
    const canvas = containerRef.current;
    const series = seriesRef.current;
    if (!canvas || !series) return null;
    const raw = series.coordinateToPrice(event.clientY - canvas.getBoundingClientRect().top);
    if (raw === null || !Number.isFinite(Number(raw))) return null;
    return snapPriceToTick(Number(raw), contract.tickSize);
  };

  const replaceAt = (order: OrderView, price: number) => {
    const snappedPrice = snapPriceToTick(price, contract.tickSize);
    if (order.limitPrice === snappedPrice || snappedPrice <= 0 || order.status === 'IN_TRANSMISSION' || order.status === 'CANCEL_PENDING' || !canTrade) return;
    onReplaceOrder(order, snappedPrice);
  };
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: '#08111e' }, textColor: '#8290a5', fontFamily: 'IBM Plex Mono' },
      grid: { vertLines: { color: '#14243a' }, horzLines: { color: '#14243a' } },
      crosshair: { vertLine: { color: '#4679b8', labelBackgroundColor: '#204f8e' }, horzLine: { color: '#4679b8', labelBackgroundColor: '#204f8e' } },
      rightPriceScale: { borderColor: '#20334e' }, timeScale: { borderColor: '#20334e', timeVisible: true, secondsVisible: true },
      localization: { priceFormatter: (price: number) => formatPrice(price, contract, language) }
    });
    chartRef.current = chart;
    const priceSeries = chartType === 'candles'
      ? chart.addSeries(CandlestickSeries, { upColor: '#237ecb', downColor: '#d88738', wickUpColor: '#58aeea', wickDownColor: '#eeaa61', borderVisible: false })
      : chart.addSeries(LineSeries, { color: '#51aef1', lineWidth: 2, priceLineVisible: true });
    seriesRef.current = priceSeries as unknown as InteractiveSeries;
    if (chartType === 'candles') {
      updatePriceRef.current = (points) => priceSeries.setData(points.map((point) => ({ time: time(point.time), open: point.open, high: point.high, low: point.low, close: point.close })));
    } else {
      updatePriceRef.current = (points) => priceSeries.setData(points.map((point) => ({ time: time(point.time), value: point.close })));
    }
    const volumes = chart.addSeries(HistogramSeries, { priceScaleId: '', priceFormat: { type: 'volume' }, color: '#274b73' });
    volumes.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    updateVolumeRef.current = (points) => volumes.setData(points.map((point) => ({ time: time(point.time), value: point.volume, color: point.close >= point.open ? '#1f659055' : '#9b5e2855' })));
    updatePriceRef.current(market.chart);
    updateVolumeRef.current(market.chart);
    chart.timeScale().fitContent();
    setChartGeneration((value) => value + 1);
    return () => { chartRef.current = null; seriesRef.current = null; updatePriceRef.current = () => undefined; updateVolumeRef.current = () => undefined; chart.remove(); };
  }, [contract.symbol, contract.quoteStyle, contract.tickSize, chartType, language]);

  useEffect(() => {
    updatePriceRef.current(market.chart);
    updateVolumeRef.current(market.chart);
    requestAnimationFrame(refreshCoordinates);
  }, [market.chart, refreshCoordinates]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(refreshCoordinates);
    });
    observer.observe(container);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [chartGeneration, refreshCoordinates]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !showMarkers) return;
    const lines = activeOrders.flatMap((order) => order.limitPrice === null ? [] : [series.createPriceLine({
      price: drag?.orderId === order.orderId ? drag.price : order.limitPrice,
      color: order.status === 'CANCEL_PENDING' ? '#718096' : order.side === 'BUY' ? '#38a7ef' : '#e58a3a',
      lineWidth: selectedOrderId === order.orderId ? 2 : 1,
      lineStyle: order.status === 'IN_TRANSMISSION' ? LineStyle.Dotted : LineStyle.Dashed,
      axisLabelVisible: true,
      title: `${order.side === 'BUY' ? copy.buyCode : copy.sellCode} ${formatInteger(order.remainingQty, language)}`
    })]);
    requestAnimationFrame(refreshCoordinates);
    return () => { for (const line of lines) series.removePriceLine(line); };
  }, [activeOrders, chartGeneration, copy, drag, language, selectedOrderId, showMarkers]);

  useEffect(() => {
    if (selectedOrderId && !activeOrders.some(({ orderId }) => orderId === selectedOrderId)) setSelectedOrderId(null);
  }, [activeOrders, selectedOrderId]);

  const symbolFills = fills.filter((fill) => fill.symbol === market.symbol).slice(-5);
  return (
    <section data-testid="market-chart" className="market-chart terminal-panel" aria-label={copy.aria(market.symbol)}>
      <header className="market-chart__head"><div><strong>{market.symbol}</strong><span>{contract.description}</span></div><div className="chart-controls"><button className={chartType === 'line' ? 'active' : ''} onClick={() => onChartType('line')}>{copy.line}</button><button className={chartType === 'candles' ? 'active' : ''} onClick={() => onChartType('candles')}>{copy.candles}</button><button className={showMarkers ? 'active' : ''} onClick={onToggleMarkers}>{copy.ordersFills}</button></div></header>
      <div className="chart-quote"><strong>{formatPrice(market.last, contract, language)}</strong><span className={market.changePct >= 0 ? 'positive' : 'negative'}>{market.changePct >= 0 ? '▲' : '▼'} {formatPct(Math.abs(market.changePct), 2, language)}</span></div>
      <div className="chart-canvas" ref={containerRef} />
      {showMarkers && activeOrders.length > 0 && <div ref={orderLayerRef} className="chart-order-layer" aria-label={copy.activeOrders}>{activeOrders.map((order) => {
        const top = coordinates[order.orderId];
        if (top === undefined) return null;
        const displayedPrice = drag?.orderId === order.orderId ? drag.price : order.limitPrice!;
        const pending = order.status === 'IN_TRANSMISSION' || order.status === 'CANCEL_PENDING';
        return <div
          key={order.orderId}
          className={`chart-order ${order.side === 'BUY' ? 'chart-order--buy' : 'chart-order--sell'} ${selectedOrderId === order.orderId ? 'is-selected' : ''} ${pending ? 'is-pending' : ''}`}
          style={{ top }}
          role="button"
          tabIndex={0}
          aria-disabled={pending || !canTrade}
          aria-label={`${order.side === 'BUY' ? copy.buy : copy.sell} LMT ${formatInteger(order.remainingQty, language)} ${order.symbol} @ ${formatPrice(displayedPrice, contract, language)}${pending ? copy.pending : ''}`}
          onClick={() => setSelectedOrderId(order.orderId)}
          onKeyDownCapture={(event) => {
            if ((event.key === 'Delete' || event.key === 'Backspace') && !pending && canTrade) { event.preventDefault(); onCancelOrder(order.orderId); }
            if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !pending && canTrade) {
              event.preventDefault();
              replaceAt(order, snapPriceToTick(displayedPrice + (event.key === 'ArrowUp' ? contract.tickSize : -contract.tickSize), contract.tickSize));
            }
          }}
          onPointerDown={(event) => {
            if (pending || !canTrade || event.button !== 0) return;
            setSelectedOrderId(order.orderId);
            event.currentTarget.setPointerCapture(event.pointerId);
            setDrag({ orderId: order.orderId, pointerId: event.pointerId, price: displayedPrice });
          }}
          onPointerMove={(event) => {
            if (drag?.orderId !== order.orderId || drag.pointerId !== event.pointerId) return;
            const price = priceAtPointer(event);
            if (price !== null) setDrag({ ...drag, price });
          }}
          onPointerUp={(event) => {
            if (drag?.orderId !== order.orderId || drag.pointerId !== event.pointerId) return;
            const price = priceAtPointer(event) ?? drag.price;
            setDrag(null);
            replaceAt(order, price);
          }}
          onPointerCancel={() => setDrag(null)}
        ><span className="chart-order__handle" aria-hidden="true">↕</span><strong>{order.side === 'BUY' ? copy.buyCode : copy.sellCode} {formatInteger(order.remainingQty, language)}</strong><span>{formatPrice(displayedPrice, contract, language)}</span><button type="button" aria-label={copy.cancelOrder(order.orderId.slice(0, 8))} disabled={pending || !canTrade} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); onCancelOrder(order.orderId); }}>×</button></div>;
      })}</div>}
      {showMarkers && symbolFills.length > 0 && <div className="chart-markers" aria-label={copy.latestFills}>{symbolFills.map((fill) => {
        const label = copy.fillLabel(fill.side === 'BUY' ? copy.buy : copy.sell, fill.quantity, formatPrice(fill.price, contract, language));
        return <span key={fill.fillId} className={fill.side === 'BUY' ? 'marker-buy' : 'marker-sell'} title={label} aria-label={label}>{fill.side === 'BUY' ? '▲' : '▼'}</span>;
      })}</div>}
      <a className="chart-attribution" href="https://www.tradingview.com/" target="_blank" rel="noreferrer">{copy.attribution}</a>
    </section>
  );
}
